import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { accountingAPI, customerAPI, userAPI } from '../../services/api';

const SalesReports = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerId, setCustomerId] = useState('');
  const [productType, setProductType] = useState('');
  const [salespersonId, setSalespersonId] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReport();
  }, [startDate, endDate, customerId, productType, salespersonId]);

  const loadInitialData = async () => {
    try {
      const [customersRes, usersRes] = await Promise.all([
        customerAPI.getAll(),
        userAPI.getAll(),
      ]);
      setCustomers(customersRes.data.data || []);
      setSalespersons(usersRes.data.data?.filter(u => u.role === 'Sales') || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };
      if (customerId) params.customer_id = customerId;
      if (productType) params.product_type = productType;
      if (salespersonId) params.salesperson_id = salespersonId;

      const response = await accountingAPI.getSalesReport(params);
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading report:', error);
    }
  };

  const exportToCSV = () => {
    if (!data?.details) return;

    const headers = ['Order #', 'Customer', 'Salesperson', 'Type', 'Qty', 'Date', 'Total', 'Discount', 'VAT', 'Fee', 'Payment Status'];
    const rows = data.details.map(item => [
      item.order_number,
      item.customer_name,
      item.salesperson_name,
      item.product_type,
      item.quantity,
      item.order_date,
      item.total_price,
      item.discount_amount,
      item.vat_amount,
      item.fee_amount,
      item.payment_status,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${startDate}_to_${endDate}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            {t('accounting.salesReports')}
          </h2>
          <p className="text-gray-600 mt-1">{t('accounting.viewSalesDetails')}</p>
        </div>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {t('accounting.exportCSV')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesOrders.customer')}
            </label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('common.all')}</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesOrders.productType')}
            </label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('common.all')}</option>
              <option value="fabric">Fabric</option>
              <option value="shoes">Shoes</option>
              <option value="cap">Cap</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesOrders.salesperson')}
            </label>
            <select
              value={salespersonId}
              onChange={(e) => setSalespersonId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('common.all')}</option>
              {salespersons.map(person => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs font-medium text-blue-600 uppercase">{t('accounting.totalOrders')}</p>
            <p className="text-2xl font-bold text-blue-900 mt-1">{data.summary.total_orders}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs font-medium text-emerald-600 uppercase">{t('accounting.totalRevenue')}</p>
            <p className="text-2xl font-bold text-emerald-900 mt-1">{t('common.currency')} {data.summary.total_revenue?.toFixed(2)}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-xs font-medium text-yellow-600 uppercase">{t('accounting.totalVAT')}</p>
            <p className="text-2xl font-bold text-yellow-900 mt-1">{t('common.currency')} {data.summary.total_vat?.toFixed(2)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-xs font-medium text-red-600 uppercase">{t('accounting.totalDiscounts')}</p>
            <p className="text-2xl font-bold text-red-900 mt-1">{t('common.currency')} {data.summary.total_discounts?.toFixed(2)}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-xs font-medium text-purple-600 uppercase">{t('accounting.totalFees')}</p>
            <p className="text-2xl font-bold text-purple-900 mt-1">{t('common.currency')} {data.summary.total_fees?.toFixed(2)}</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <p className="text-xs font-medium text-indigo-600 uppercase">{t('accounting.avgOrderValue')}</p>
            <p className="text-2xl font-bold text-indigo-900 mt-1">{t('common.currency')} {data.summary.average_order_value?.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Details Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.orderNumber')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.salesperson')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.productType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.orderDate')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.totalPrice')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  VAT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.paymentStatus')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.details && data.details.length > 0 ? (
                data.details.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                      {order.order_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.customer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {order.salesperson_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                      {order.product_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {order.order_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                      {t('common.currency')} {order.total_price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right">
                      -{t('common.currency')} {order.discount_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 text-right">
                      +{t('common.currency')} {order.vat_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {t(`salesOrders.paymentStatuses.${order.payment_status}`)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesReports;
